// AVRASTRO AI Content Studio — export marking (EU AI Act art. 50; FEASIBILITY.md §4) and PDF export. Pure, browser-safe.
//  - JPEG: an XMP packet (APP1) with the IPTC «digital source type» (trainedAlgorithmicMedia / compositeWithTrainedAlgorithmicMedia
//    for AI-generated pictures, digitalCreation otherwise), the asset id, its approved hash and the models used — the
//    machine-readable marking the major platforms read today;
//  - PNG: the same facts as iTXt/tEXt chunks (XMP in «XML:com.adobe.xmp»);
//  - PDF: one page holding the JPEG (DCTDecode), with the provenance in the document information;
//  - C2PA / Content Credentials manifests are NOT produced: they need a signing certificate from a C2PA trust list. The
//    provenance is recorded on the asset instead and the UI says so (docs/content-studio/FEASIBILITY.md «Status»).

import { crc32 } from './zip.ts';

export interface ExportProvenance { assetId: string; hash: string | null; aiGenerated: boolean; models: string[]; creator: string | null; label: string | null; createdAt: string }

export const IPTC_SOURCE = {
  ai: 'http://cv.iptc.org/newscodes/digitalsourcetype/trainedAlgorithmicMedia',
  composite: 'http://cv.iptc.org/newscodes/digitalsourcetype/compositeWithTrainedAlgorithmicMedia',
  digital: 'http://cv.iptc.org/newscodes/digitalsourcetype/digitalCreation',
} as const;
const xmlEsc = (s: string) => String(s ?? '').replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' })[c]!);

/** the XMP packet of an export (our creatives are composites: AI picture + our own typography) */
export function provenanceXmp(p: ExportProvenance): string {
  const src = p.aiGenerated ? IPTC_SOURCE.composite : IPTC_SOURCE.digital;
  return `<?xpacket begin="﻿" id="W5M0MpCehiHzreSzNTczkc9d"?>
<x:xmpmeta xmlns:x="adobe:ns:meta/"><rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">
<rdf:Description rdf:about="" xmlns:Iptc4xmpExt="http://iptc.org/std/Iptc4xmpExt/2008-02-29/" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:xmp="http://ns.adobe.com/xap/1.0/" xmlns:avrastro="https://avrastro.com/ns/studio/1.0/"
 Iptc4xmpExt:DigitalSourceType="${src}" xmp:CreatorTool="AVRASTRO Studio" xmp:CreateDate="${xmlEsc(p.createdAt)}" avrastro:assetId="${xmlEsc(p.assetId)}" avrastro:approvedHash="${xmlEsc(p.hash ?? '')}" avrastro:aiGenerated="${p.aiGenerated}" avrastro:models="${xmlEsc(p.models.join(', '))}">
${p.creator ? `<dc:creator><rdf:Seq><rdf:li>${xmlEsc(p.creator)}</rdf:li></rdf:Seq></dc:creator>` : ''}${p.label ? `<dc:description><rdf:Alt><rdf:li xml:lang="x-default">${xmlEsc(p.label)}</rdf:li></rdf:Alt></dc:description>` : ''}
</rdf:Description></rdf:RDF></x:xmpmeta>
<?xpacket end="w"?>`;
}

const utf8 = (s: string) => new TextEncoder().encode(s);
function concat(parts: Uint8Array[]): Uint8Array { const n = parts.reduce((a, p) => a + p.length, 0); const out = new Uint8Array(n); let o = 0; for (const p of parts) { out.set(p, o); o += p.length; } return out; }

/** insert an XMP APP1 segment after the JPEG's SOI (and after a JFIF APP0 when there is one) */
export function jpegWithXmp(jpeg: Uint8Array, xmp: string): Uint8Array {
  if (jpeg[0] !== 0xff || jpeg[1] !== 0xd8) throw new Error('not_jpeg');
  const ns = utf8('http://ns.adobe.com/xap/1.0/\0');
  const body = utf8(xmp);
  const len = ns.length + body.length + 2;
  if (len > 65535) throw new Error('xmp_too_large');
  const seg = concat([new Uint8Array([0xff, 0xe1, len >> 8, len & 0xff]), ns, body]);
  let at = 2;
  if (jpeg[2] === 0xff && jpeg[3] === 0xe0) at = 4 + ((jpeg[4]! << 8) | jpeg[5]!);
  return concat([jpeg.subarray(0, at), seg, jpeg.subarray(at)]);
}
/** read back the XMP packet of a JPEG (tests, and the asset viewer) */
export function jpegXmp(jpeg: Uint8Array): string | null {
  let i = 2;
  while (i + 4 < jpeg.length && jpeg[i] === 0xff) {
    const m = jpeg[i + 1]!, len = (jpeg[i + 2]! << 8) | jpeg[i + 3]!;
    if (m === 0xe1) { const t = new TextDecoder().decode(jpeg.subarray(i + 4, i + 2 + len)); if (t.startsWith('http://ns.adobe.com/xap/1.0/')) return t.slice(29); }
    if (m === 0xda) break;
    i += 2 + len;
  }
  return null;
}

// --- PNG ------------------------------------------------------------------------------------------------------------------
function chunk(type: string, data: Uint8Array): Uint8Array {
  const t = utf8(type);
  const len = new Uint8Array([data.length >>> 24, (data.length >>> 16) & 0xff, (data.length >>> 8) & 0xff, data.length & 0xff]);
  const crc = crc32(concat([t, data]));
  return concat([len, t, data, new Uint8Array([crc >>> 24, (crc >>> 16) & 0xff, (crc >>> 8) & 0xff, crc & 0xff])]);
}
/** add iTXt chunks (UTF-8 text) right after IHDR */
export function pngWithText(png: Uint8Array, entries: Record<string, string>): Uint8Array {
  const sig = [137, 80, 78, 71, 13, 10, 26, 10];
  if (!sig.every((v, i) => png[i] === v)) throw new Error('not_png');
  const ihdrLen = (png[8]! << 24) | (png[9]! << 16) | (png[10]! << 8) | png[11]!;
  const at = 8 + 12 + ihdrLen;
  const chunks = Object.entries(entries).map(([k, v]) => chunk('iTXt', concat([utf8(k.slice(0, 79)), new Uint8Array([0, 0, 0, 0, 0]), utf8(v)])));
  return concat([png.subarray(0, at), ...chunks, png.subarray(at)]);
}
/** the iTXt / tEXt entries of a PNG */
export function pngText(png: Uint8Array): Record<string, string> {
  const out: Record<string, string> = {};
  let i = 8;
  while (i + 8 <= png.length) {
    const len = (png[i]! << 24 >>> 0) + (png[i + 1]! << 16) + (png[i + 2]! << 8) + png[i + 3]!;
    const type = String.fromCharCode(png[i + 4]!, png[i + 5]!, png[i + 6]!, png[i + 7]!);
    const data = png.subarray(i + 8, i + 8 + len);
    if (type === 'iTXt' || type === 'tEXt') {
      const z = data.indexOf(0);
      const key = new TextDecoder().decode(data.subarray(0, z));
      out[key] = new TextDecoder().decode(type === 'iTXt' ? data.subarray(z + 5) : data.subarray(z + 1));
    }
    if (type === 'IEND') break;
    i += 12 + len;
  }
  return out;
}

// --- PDF ------------------------------------------------------------------------------------------------------------------
/** a one-page PDF with the JPEG at its pixel size (72 dpi points), the provenance in the document information */
export function jpegToPdf(jpeg: Uint8Array, w: number, h: number, info: { title: string; subject?: string; keywords?: string }): Uint8Array {
  const pdfStr = (s: string) => `(${String(s).replace(/[\\()]/g, (c) => '\\' + c).replace(/[^\x20-\x7e]/g, '?')})`;
  // text strings in UTF-16BE (Greek titles survive)
  const u16 = (s: string) => { let hex = 'FEFF'; for (const ch of String(s)) { const cp = ch.codePointAt(0)!; if (cp > 0xffff) { const v = cp - 0x10000; hex += (0xd800 + (v >> 10)).toString(16).padStart(4, '0') + (0xdc00 + (v & 0x3ff)).toString(16).padStart(4, '0'); } else hex += cp.toString(16).padStart(4, '0'); } return `<${hex.toUpperCase()}>`; };
  const content = `q ${w} 0 0 ${h} 0 0 cm /Im0 Do Q`;
  const objs: (string | Uint8Array)[][] = [
    ['<< /Type /Catalog /Pages 2 0 R >>'],
    ['<< /Type /Pages /Kids [3 0 R] /Count 1 >>'],
    [`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${w} ${h}] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>`],
    [`<< /Type /XObject /Subtype /Image /Width ${w} /Height ${h} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\nstream\n`, jpeg, '\nendstream'],
    [`<< /Length ${content.length} >>\nstream\n${content}\nendstream`],
    [`<< /Title ${u16(info.title)} /Producer ${pdfStr('AVRASTRO Studio')}${info.subject ? ` /Subject ${u16(info.subject)}` : ''}${info.keywords ? ` /Keywords ${u16(info.keywords)}` : ''} >>`],
  ];
  const parts: Uint8Array[] = [utf8('%PDF-1.4\n%\xe2\xe3\xcf\xd3\n')];
  const offsets: number[] = [];
  let pos = parts[0]!.length;
  objs.forEach((o, i) => {
    offsets.push(pos);
    const pieces = [utf8(`${i + 1} 0 obj\n`), ...o.map((x) => (typeof x === 'string' ? utf8(x) : x)), utf8('\nendobj\n')];
    for (const p of pieces) { parts.push(p); pos += p.length; }
  });
  const xref = `xref\n0 ${objs.length + 1}\n0000000000 65535 f \n${offsets.map((o) => String(o).padStart(10, '0') + ' 00000 n \n').join('')}trailer\n<< /Size ${objs.length + 1} /Root 1 0 R /Info 6 0 R >>\nstartxref\n${pos}\n%%EOF\n`;
  parts.push(utf8(xref));
  return concat(parts);
}
